import React from 'react';
import {BingoCard} from "../BingoCard";
import ErrorBoundary from "Components/ErrorBoundary";
import {Box, makeStyles} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import clsx from "clsx";
import useBingo from "Hooks/useBingo";
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import "swiper/css/navigation"
import SwiperCore, {Navigation} from 'swiper';
import {BP_LARGE} from "Theme/breakpoints";
import {getRoomImageFromRoomObject} from "Components/Bingo/helpers";
import {BINGO_MAINTENANCE, IS_DF_BINGO_ENABLED} from "Setup/config";
import extendedDayjs from "Helpers/dayjs";
import temporarySetBingoRoomMultiOpeningTime from "Helpers/temporarySetBingoRoomMultiOpeningTime";
import useGames from "Hooks/useGames";

SwiperCore.use([Navigation]);
window.dayjs = extendedDayjs;

export const PragmaticRoom = ({room, marginBottom = false, ...rest}) => {
    if (!rest.isLoaded) {
        return (
            <ErrorBoundary>
                <BingoCard isLoaded={false} marginBottom={marginBottom}/>
            </ErrorBoundary>
        );
    }

    const {roomName} = room;
    const roomImage = getRoomImageFromRoomObject(room);

    room = temporarySetBingoRoomMultiOpeningTime(room);

    return (
        <ErrorBoundary>
            <BingoCard
                isLoaded
                isAnimated={room.isAnimated}
                roomId={room.id}
                image={roomImage}
                gameType={room.bingoType}
                roomName={roomName}
                playersInRoom={room.playersInRoom}
                cardPrice={room.cardCost * 100}
                currencyMinor={room.currencyMinor}
                currencyMajor={room.currencyMajor}
                gamePrize={room.gamePrize ? room.gamePrize.toFixed(2) : false}
                jackpotAmount={room.jackpotAmount ? room.jackpotAmount.toFixed(2) : false}
                nextGameCountdownInSeconds={room.open ? room.nextGameSec : room.secondsTilOpen}
                roomOpen={BINGO_MAINTENANCE ? false : room.open}
                roomSlug={room.slug}
                marginBottom={marginBottom}
                catalogueGameId={room.cgid}
            />
        </ErrorBoundary>
    );
};

const useBingoRoomsPanelStyles = makeStyles(theme => {
    return {
        swiperContainer: {
            '& .swiper-slide': {
                marginBottom: theme.spacing(2),
                maxWidth: 300
            },
            '& .swiper-wrapper': {
                paddingLeft: '1px'
            },
            '& .swiper-button-disabled': {
                display: "none"
            }
        },
        swiperButton: {
            cursor: "pointer",
            height: theme.spacing(4),
            width: theme.spacing(4),
            position: "absolute",
            top: `calc(50% - ${theme.spacing(3)}px)`,
            zIndex: 1,
            borderRadius: 50,
            boxShadow: "0 1px 2px rgb(0 0 0 / 15%)",
            background: "white",
            '& .MuiSvgIcon-root': {
                fontSize: theme.spacing(4),
                color: theme.palette.primary.main
            },

            [theme.breakpoints.up(BP_LARGE)]: {
                height: theme.spacing(6),
                width: theme.spacing(6),
                '& .MuiSvgIcon-root': {
                    fontSize: theme.spacing(6),
                    color: theme.palette.primary.main
                },
            },
            '&.swiper-button-prev': {
                left: -15,
                [theme.breakpoints.up(BP_LARGE)]: {
                    left: -20
                },
            },
            '&.swiper-btn-next': {
                right: -10
            },
            '&.swiper-button-disabled': {
                opacity: 0.5
            },
            '&:hover': {
                '&:not(.swiper-button-disabled)': {
                    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.20)"
                }
            }
        }
    }
});

const sliderBreakpoints = {
    1500: {
        slidesPerView: 4.2,
    },
    1360: {
        slidesPerView: 3.5,
    },
    1280: {
        slidesPerView: 3.2,
    },
    1024: {
        slidesPerView: 2.5
    },
    768: {
        slidesPerView: 3
    },
    640: {
        slidesPerView: 2.5
    },
    480: {
        slidesPerView: 2
    }
};

const sliderSettings = {
    slidesPerView: 1.5,
    spaceBetween: 15,
    breakpoints: sliderBreakpoints,
    navigation: {
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev'
    }
};

const FallbackDFRoom = ({image, roomName, roomId, handleRoomLaunch}) => {
    return (
        <BingoCard
            minimal
            isLoaded
            image={image}
            roomName={roomName}
            playersInRoom={0}
            cardPrice={0}
            currencyMinor="p"
            currencyMajor="£"
            roomOpen
            onClick={handleRoomLaunch}
        />
    );
};

export const DragonfishRoom = (props) => {
    const {launchBingoGame} = useGames();

    const {image, roomName, roomId} = props;
    const handleRoomLaunch = () => launchBingoGame(roomId);
    const fallbackProps = {image, roomName, roomId, handleRoomLaunch};

    if (props.hasError) return <FallbackDFRoom {...fallbackProps}/>;

    return (
        <BingoCard
            minimal={!props?.NumberOfPlayers || props.minimal}
            isLoaded
            image={image}
            roomName={roomName}
            playersInRoom={props?.NumberOfPlayers ?? 0}
            cardPrice={props?.CardValue ? Number(props.CardValue * 100) : 0}
            currencyMinor="p"
            currencyMajor="£"
            gameType={props?.gameType}
            gamePrize={props?.JackpotAmount ?? false}
            jackpotAmount={props?.ProgressiveJackpotAmount ?? false}
            nextGameCountdownInSeconds={props?.SecondsToGame ? props.SecondsToGame : 0}
            roomOpen={props.open}
            onClick={handleRoomLaunch}
        />
    );
};

const BingoPanelSlider = (props) => {
    const {roomFeed: bingoRoomData, bingoRoomsOrder} = useBingo();
    const classes = useBingoRoomsPanelStyles();


    if (props.roomsLoaded) {
        return (
            <Box className={classes.swiperContainer}>
                {(bingoRoomData.length > 0) &&
                    <Swiper navigation={true} {...sliderSettings}>
                        {bingoRoomsOrder.map(roomGroup => roomGroup.rooms.map(room => {
                            if(!roomGroup.isActive) return null;
                            const Component = roomGroup.roomType === 'DragonFishBingo' ? DragonfishRoom : PragmaticRoom;
                            const props = roomGroup.roomType === 'DragonFishBingo' ? {...room} : {room};

                            return (
                                <SwiperSlide key={`bingo-room-slide-${room.roomId}`}>
                                    <ErrorBoundary>
                                        <Component {...props} isLoaded/>
                                    </ErrorBoundary>
                                </SwiperSlide>
                            )
                        }))}
                    </Swiper>
                }
                <div className={clsx(classes.swiperButton, "swiper-btn-next")}><ChevronRightIcon/></div>
                <div className={clsx(classes.swiperButton, "swiper-btn-prev")}><ChevronLeftIcon/></div>
            </Box>
        )
    }
    return null;
}

const BingoRoomsPanel = props => {
    return (
        <Box position="relative">
            {!props.roomsLoaded &&
                <Swiper navigation={true} {...sliderSettings}>
                    <SwiperSlide><PragmaticRoom marginBottom isLoaded={false}/></SwiperSlide>
                    <SwiperSlide><PragmaticRoom marginBottom isLoaded={false}/></SwiperSlide>
                    <SwiperSlide><PragmaticRoom marginBottom isLoaded={false}/></SwiperSlide>
                    <SwiperSlide><PragmaticRoom marginBottom isLoaded={false}/></SwiperSlide>
                    <SwiperSlide><PragmaticRoom marginBottom isLoaded={false}/></SwiperSlide>
                </Swiper>
            }
            <BingoPanelSlider roomsLoaded={props.roomsLoaded}/>
        </Box>
    );
};

export default BingoRoomsPanel;
